/**
 * Paiements SOL API Client
 * 
 * Endpoints:
 * - POST /api/transactions/paiements-sol/ - Create paiement
 * - GET /api/transactions/paiements-sol/list/ - List paiements
 * - POST /api/transactions/paiements-sol/aggregated/ - Get aggregated data
 */
import { apiClient } from './client';

// Types
export interface PaiementSOL {
    id: string;
    compte_id: string;
    compte_code?: string;  // Enriched from client-service
    client_nom?: string;   // Enriched from client-service
    nb_main: number;
    montant: number;
    created_at: string;
    created_by_id: string;
    date?: string;
    updated_at?: string;
}

export interface PaiementListResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: PaiementSOL[];
}

export interface CreatePaiementRequest {
    compte_id: string;
    montant: number;
}

export interface CreatePaiementResponse {
    message: string;
    paiement_id: string;
    transaction_id: string;
    pdf_base64?: string;  // PDF receipt for display/print
    paiement?: {  // Optional full paiement object
        id: string;
    };
    details: {
        compte_code: string;
        client_nom: string;
        montant_peye: number;
        montant_brut: number;
        frais: number;
        montant_net: number;
        total_deja_peye: number;
        reste_a_peye: number;
    };
}

export interface PaiementAggregatedResponse {
    compte_id: string;
    compte_code: string;
    client_nom: string;
    nb_main: number;
    montant_jours: number;
    duree_sol: number;
    montant_brut: number;
    frais: number;
    fee_percentage: number;
    montant_net: number;
    solde_S: number;
    total_peye: number;
    reste_a_peye: number;
    dette_bousanm: number;
    statut: string;
    paiements: Array<{
        id: string;
        montant: number;
        nb_main: number;
        date: string;
    }>;
}

// API Functions
export const paiementsApi = {
    /**
     * Create a new SOL payment
     */
    create: async (data: CreatePaiementRequest): Promise<CreatePaiementResponse> => {
        const response = await apiClient.post('/api/transactions/paiements-sol/', data);
        return response.data;
    },

    /**
     * List all paiements (with optional filtering)
     */
    list: async (params?: {
        compte_id?: string;
        page?: number;
        page_size?: number;
    }): Promise<PaiementListResponse> => {
        const response = await apiClient.get('/api/transactions/paiements-sol/list/', { params });
        return response.data;
    },

    /**
     * Get aggregated paiement data for a compte (debt status)
     */
    getAggregated: async (compte_id: string): Promise<PaiementAggregatedResponse> => {
        const response = await apiClient.post('/api/transactions/paiements-sol/aggregated/', { compte_id });
        return response.data;
    },
};

export default paiementsApi;
